import React from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import {
  Avatar,
  makeStyles,
  createStyles,
  Theme,
  Tooltip,
  Switch,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import Brightness4Icon from '@material-ui/icons/Brightness4';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IState } from '../data/types';
import { getInitials } from '../utils/stringHelpers';
import { handleLogout } from '../data/coreActions';
import { localRoutes } from '../data/constants';
import { isStudent, roleLabel } from '../data/appRoles';
import { colors } from '../theme/tokens';
import { useThemeMode } from '../theme/ThemeModeProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    trigger: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
      padding: '4px 8px',
      borderRadius: 8,
      transition: 'background 0.15s ease',
      '&:hover': {
        background: colors.hover,
      },
    },
    triggerDetailed: { flexDirection: 'row-reverse', gap: 12 },
    avatarDetailed: { width: 44, height: 44, fontSize: 15 },
    identity: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      textAlign: 'right',
      [theme.breakpoints.down('xs')]: { display: 'none' },
    },
    role: { fontSize: 12, color: colors.navActiveText },
    avatar: {
      width: 32,
      height: 32,
      fontSize: 13,
      fontWeight: 700,
      background: `linear-gradient(135deg, ${colors.progressFill} 0%, ${colors.accentOrange} 100%)`,
    },
    name: {
      fontSize: '13px',
      fontWeight: 600,
      color: colors.textPrimary,
      maxWidth: 160,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    menuItem: {
      fontSize: '13px',
      fontWeight: 500,
      minWidth: 140,
    },
    themeItem: {
      fontSize: '13px',
      fontWeight: 500,
      minWidth: 140,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    themeItemLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
  }),
);

export const BarView = (props: any) => {
  const classes = useStyles();
  const profile = useSelector((state: IState) => state.core.user);
  const dispatch = useDispatch();
  const history = useHistory();
  const { mode, toggleMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  function doLogout() {
    dispatch(handleLogout());
  }

  function handleMenu(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleCloseMenu() {
    setAnchorEl(null);
  }

  function goToProfile() {
    handleCloseMenu();
    history.push(localRoutes.myProfile);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {isStudent(profile) && (
        <Tooltip title="Mark Attendance">
          <IconButton
            size="small"
            onClick={() => history.push(localRoutes.attendanceCode)}
            style={{ color: colors.progressFill }}
          >
            <HowToRegIcon style={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
      )}
      <div
        className={`${classes.trigger} ${
          props.detailed ? classes.triggerDetailed : ''
        }`}
        onClick={handleMenu}
        aria-controls="menu-appbar"
        aria-haspopup="true"
        role="button"
      >
        <Avatar
          className={`${classes.avatar} ${
            props.detailed ? classes.avatarDetailed : ''
          }`}
          src={profile?.avatar || ''}
        >
          {getInitials(profile.fullName || '')}
        </Avatar>
        <span className={classes.identity}>
          <span className={classes.name}>{profile.fullName}</span>
          {props.detailed && (
            <span className={classes.role}>{roleLabel(profile)}</span>
          )}
        </span>
      </div>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        getContentAnchorEl={null}
        open={menuOpen}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={goToProfile} className={classes.menuItem}>
          My Profile
        </MenuItem>
        <MenuItem onClick={toggleMode} className={classes.themeItem}>
          <span className={classes.themeItemLabel}>
            <Brightness4Icon style={{ fontSize: 17 }} />
            Dark Mode
          </span>
          <Switch
            checked={mode === 'dark'}
            onChange={toggleMode}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
        </MenuItem>
        <MenuItem onClick={doLogout} className={classes.menuItem}>
          Sign out
        </MenuItem>
      </Menu>
    </div>
  );
};
